import { SignUpType } from './enums/signUpType';

/**
 * Who funds a WABA's conversations — the credit line "Attach Credit Line" in
 * Settings > Billing hooks the number up to. Decided by the sign-up type,
 * because that is what recorded which BSP / Meta solution the number was
 * onboarded through. sign_up_type is written from the signup form (and its
 * column default is `heltar`), so it only says something where the embedded
 * signup completed; the controller's WhatsApp-details gate screens out rows
 * that never did.
 *
 * - ROUTE_MOBILE: onboarded on the Route Mobile solution (live since Mar 2026;
 *   the "Heltar" signup tab has also submitted route_mobile since Aug 18 2026,
 *   PR #789). Funded from Route Mobile's India line, attached via the Meta
 *   Graph API.
 * - HELTAR and KARIX are both Karix BSP numbers and both attach Karix's line
 *   through Karix's TSP API. The two values only record which messaging API
 *   the number uses: HELTAR is the Graph-API flavour (Heltar tab, Oct 2025 to
 *   Aug 17 2026, on Karix's Meta solution), KARIX is Karix's own wrapper API
 *   (retired tab, last signup May 2026). Karix's invoices carry both.
 * - Everything else is either the customer's own funding (EMBEDDED — their
 *   card is on the account) or a partner BSP that bills them directly
 *   (First Connect, Tata, Kaleyra, ValueFirst, VConnect, Masiv, Onextel) or a
 *   CUSTOM connect with no BSP we can speak for. Nothing to attach.
 *
 * The BSP part of OUR_BSP_SIGN_UP_TYPES in billing: the BSPs we pay the
 * provider for are exactly the ones we can attach credit for. Billing also
 * covers CUSTOM and EMBEDDED, which have no line of ours to attach.
 */
export type CreditLineProvider = 'route_mobile' | 'karix';

const CREDIT_LINE_PROVIDER_BY_SIGN_UP_TYPE: Partial<
  Record<SignUpType, CreditLineProvider>
> = {
  [SignUpType.ROUTE_MOBILE]: 'route_mobile',
  [SignUpType.HELTAR]: 'karix',
  [SignUpType.KARIX]: 'karix',
};

export const CREDIT_LINE_PROVIDER_LABEL: Record<CreditLineProvider, string> = {
  route_mobile: 'Route Mobile',
  karix: 'Karix',
};

export const getCreditLineProvider = (
  signUpType: SignUpType | string | null | undefined,
): CreditLineProvider | null => {
  if (!signUpType) return null;
  return CREDIT_LINE_PROVIDER_BY_SIGN_UP_TYPE[signUpType as SignUpType] ?? null;
};

/** Sign-up types the credit line button works for, for error messages. */
export const CREDIT_LINE_SIGN_UP_TYPES: readonly SignUpType[] = Object.keys(
  CREDIT_LINE_PROVIDER_BY_SIGN_UP_TYPE,
) as SignUpType[];
